import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import BodyContainer from "../components/BodyContainer";
import BottomContainer from "../components/BottomContainer";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import FooterLinks from "../components/FooterLinks";
import { Helmet } from "react-helmet-async";
import bannerImage from "../assets/banner4.png";
import "../Profile.css";
import "../Site.css";

function Profile() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [productNames, setProductNames] = useState({});
  const [testScores, setTestScores] = useState([]);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementText, setAgreementText] = useState("");
  const [canAgree, setCanAgree] = useState(false);
  const scrollBoxRef = useRef(null);

  const isDataFetched = useRef(false);
  const API_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    async function checkAgreements() {
      try {
        const res = await fetch(`${API_URL}/api/agreements/${user.Id}`);
        const body = await res.json();
        if (res.status === 404 || (body?.agreements?.length === 0)) {
          setShowAgreementModal(true);
        }
      } catch (err) {
        console.error("Error checking agreements:", err);
        setShowAgreementModal(true);
      }
    }

    async function fetchAgreementText() {
      try {
        const response = await fetch("/assets/terms_and_privacy.txt");
        const text = await response.text();
        setAgreementText(text);
      } catch (err) {
        console.error("Error loading terms and privacy file:", err);
      }
    }

    checkAgreements();
    fetchAgreementText();
  }, [user, navigate]);

  useEffect(() => {
    if (!user || isDataFetched.current) return;
    isDataFetched.current = true;

    async function fetchSubscriptions() {
      try {
        const response = await fetch(`${API_URL}/api/subscriptions/${user.Id}/active`);
        if (!response.ok) throw new Error("Failed to fetch subscriptions");

        const data = await response.json();
        setSubscriptions(data.subscriptions || []);

        const productIds = [...new Set(data.subscriptions.flatMap((sub) => sub.Products))];
        if (productIds.length > 0) {
          fetchProductNames(productIds);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    async function fetchRecentTestScores() {
      try {
        const response = await fetch(`${API_URL}/api/testscores/${user.Id}`);
        if (!response.ok) throw new Error("Failed to fetch test scores");

        const data = await response.json();
        const sortedScores = data.testScores
          .sort((a, b) => new Date(b.Created) - new Date(a.Created))
          .slice(0, 3);
        setTestScores(sortedScores);
      } catch (err) {
        console.error("Error fetching test scores:", err);
      }
    }

    fetchSubscriptions();
    fetchRecentTestScores();
  }, [user]);

  async function fetchProductNames(productIds) {
    try {
      const response = await fetch(`${API_URL}/api/products`);
      if (!response.ok) throw new Error("Failed to fetch product names");

      const data = await response.json();
      const map = {};
      data.forEach((p) => {
        if (productIds.includes(p.Id)) {
          map[p.Id] = p.Name;
        }
      });
      setProductNames(map);
    } catch (err) {
      console.error("Error fetching product names:", err);
    }
  }

  const handleAgree = async () => {
    const payload = {
      userId: user.Id,
      termsAccepted: true,
      privacyAccepted: true,
    };

    try {
      const res = await fetch(`${API_URL}/api/agreements/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`Server responded with status ${res.status}`);
      }

      setShowAgreementModal(false);
    } catch (err) {
      console.error("Error saving agreement:", err);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem("user");
    navigate("/login");
  };

  const calculatePercentage = (score, total) => {
    return total === 0 ? score : Math.round((score / total) * 100);
  };

  return (
    <>
      <Helmet>
        <title>PharmPrepPro - Profile</title>
      </Helmet>

      <BottomContainer>
        <Banner height="300px" image={bannerImage} />

        <BodyContainer>
          <div className="profile-container">
            <h1 className="profile-name">{user?.Name}</h1>
            <p className="profile-email">{user?.Email}</p>

            <div className="subscriptions-container">
              <h2 className="subscriptions-title">Active Subscriptions</h2>
              {loading ? (
                <p>Loading subscriptions...</p>
              ) : error ? (
                <p className="error-message">{error}</p>
              ) : subscriptions.length === 0 ? (
                <p className="no-subscriptions">You have no active subscriptions.</p>
              ) : (
                <div className="subscription-list">
                  {subscriptions.map((sub) => (
                    <div key={sub.Id} className="subscription-card">
                      <p className="subscription-title">
                        {sub.Products.map((id) => productNames[id] || "Unknown Product").join(", ")}
                      </p>
                      <p className="subscription-expiration">
                        Expires: {new Date(sub.ExpirationDate).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="test-scores-container">
              <h2 className="test-scores-title">Recent Test Scores</h2>
              {testScores.length === 0 ? (
                <p className="no-test-scores">No test scores available.</p>
              ) : (
                <ul className="test-scores-list">
                  {testScores.map((score) => (
                    <li key={score.Id} className="test-score-card">
                      <p className="test-score-name"><strong>Test:</strong> {score.TestName || "Unknown Test"}</p>
                      <p className="test-score-value"><strong>Score:</strong> {calculatePercentage(score.Score, score.TotalQuestions)}%</p>
                      <p className="test-score-date"><strong>Date:</strong> {new Date(score.Created).toLocaleString()}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button className="signout-button" onClick={handleSignOut}>Sign Out</button>
          </div>
        </BodyContainer>
      </BottomContainer>

      <FooterLinks />
      <Footer />

      {showAgreementModal && (
        <div className="agreement-modal-overlay">
          <div className="agreement-modal-content">
            <h2>Terms of Service & Privacy Policy</h2>

            <div
              className="agreement-scrollable-box"
              ref={scrollBoxRef}
              onScroll={() => {
                const el = scrollBoxRef.current;
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
                  setCanAgree(true);
                }
              }}
            >
              <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                {agreementText}
              </pre>
            </div>

            <button
              className="agree-button"
              onClick={handleAgree}
              disabled={!canAgree}
            >
              I Agree
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default Profile;
